import type { AuthResponse, SignInDTO, SignUpDTO, Invitation, CreateInvitationDTO, ValidateInviteResponse, ChoiceSetWithOptions, TripItemFile, Country, BlogPost, BlogCategory, BlogTag, BlogPostsResponse, CreateBlogPostDTO, UpdateBlogPostDTO, CreateBlogCategoryDTO, UpdateBlogCategoryDTO, CreateBlogTagDTO, BlogAuthorProfile, UpdateBlogAuthorDTO, GenerateBlogPostDTO, BlogGenerationJob, DestinationBlogPost, TripConfirmation, Lead, CreateLeadDTO, CreateAdminLeadDTO, CreateTripRequestDTO, UpdateLeadDTO, ConvertLeadToClientDTO, ConvertLeadToClientResponse, UpdateLeadProfileDTO, LeadTripRequest, LeadTripRequestInput, DraftLeadFirstResponseDTO, LeadFirstResponseDraft, TravelProfile, UpdateTravelProfileDTO, ExtractedItemCandidate, NewsletterSubscribeDTO, AdminDeadlinesResponse, AdminDashboard, AdminSearchKind, AdminSearchResponse, ClientLeadSummary, ClientNote, CreateClientNoteDTO, UpdateClientNoteDTO, Client, ClientSummary, ClientListParams, ClientListResponse, CreateClientDTO, UpdateClientDTO, ClientRelationship, CreateClientRelationshipDTO, ClientByProfileResponse, ClientTrip, DreamCaptureRequest, DreamStageRequest, DreamCaptureListResponse, DreamListQuery, DreamSummaryResponse, ConversationsResponse, ConversationDetail, ConversationFilter, Communication, CommunicationTriage, CommunicationAudioResponse, SendConversationMessageDTO, UpdateConversationDTO, UpdateTriageDTO, KbDestination, KbDestinationSummary, KbDestinationListParams, KbDestinationListResponse, KbMatchResponse, KbMatchManyResponse, CreateKbDestinationDTO, UpdateKbDestinationDTO, KbProposed, KbReviewItem, KbReviewRequest, KbReviewResponse, KbFact, KbFactInput, KbEntry, KbEntryInput, KbTip, KbTipInput, KbSource, CreateKbSourceDTO, KbPlacesRollup, KbWikivoyageLookup, UserRole, SocialAsset, SocialBrandGuide, SocialExport, SocialMessage, SocialMessageDetail, SocialRendering, SocialSession, SocialSessionListParams, SocialSessionListResponse, SocialTemplate, SocialTemplateListResponse, SocialFormat, CreateSocialSessionDTO, UpdateSocialSessionDTO, CreateSocialMessageDTO, CreateSocialMessageResponse, CreateSocialTemplateDTO, UpdateSocialTemplateDTO, TripTemplateCollection, TripTemplateStartingPoint, AdminTripTemplate, AdminTripTemplateListResponse, TripTemplateInput, TripTemplateCollectionInput, SupplierAffiliation, SupplierAffiliationInput, SupplierCapture, SupplierCaptureListParams, SupplierCaptureListResponse, SupplierContact, SupplierContactInput, SupplierDirectoryParams, SupplierDirectoryResponse, SupplierFollowUp, SupplierFollowUpFilter, SupplierFollowUpInput, SupplierFollowUpListResponse, SupplierInteraction, SupplierInteractionInput, SupplierIntroduction, SupplierIntroductionInput, SupplierOrganization, SupplierOrganizationInput, SupplierOrganizationLink, SupplierOrganizationLinkInput, SupplierProposalQueue, SupplierReviewItem, SupplierReviewRequest, SupplierReviewResponse, CreateSupplierCaptureDTO, Task, TaskListQuery, TaskListResponse, TaskInput, TaskUpdate, TaskCompleteResponse, TodoistStatus, TodoistSyncMode, TripTasksResponse, GenerateTripTasksRequest, TaskRuleUsageResponse, TripTaskReview, TaskSuggestionReviewItem, RouteMapSpec, RouteMapThemeName, TripRouteMapResponse } from '@wanderlust/shared';
import { safeStorage } from './safeStorage';

// Use /api prefix for all API calls (proxied in dev, same-origin in prod)
const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  status: number;
  /**
   * The machine-readable `code` the API sent alongside `error`, when it sent
   * one. Callers that need to tell two failures with the same status apart
   * (a challenge that never arrived vs. one Cloudflare turned down) branch on
   * this rather than on the human-readable message.
   */
  code?: string;
  /**
   * The whole error body, for the few failures that carry more than a
   * sentence: a supplier review answers 409 with the values still to be
   * decided, and the screen has to show them rather than the message alone.
   */
  body?: unknown;

  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// Track if a refresh is in progress to avoid multiple simultaneous refreshes
let refreshPromise: Promise<boolean> | null = null;

// Broadcast token updates to the browser extension
// This allows the extension to stay in sync without racing on refresh tokens
function broadcastTokensToExtension(tokens: {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}) {
  window.postMessage({
    type: 'WANDERLUST_TOKENS_UPDATED',
    tokens,
  }, window.location.origin);
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = safeStorage.getItem('refresh_token');
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    safeStorage.setItem('access_token', data.access_token);
    safeStorage.setItem('refresh_token', data.refresh_token);
    safeStorage.setItem('expires_at', data.expires_at.toString());

    // Notify extension of new tokens
    broadcastTokensToExtension({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
    });

    return true;
  } catch {
    return false;
  }
}

async function refreshTokenIfNeeded(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = tryRefreshToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function request<T>(
  path: string,
  options?: RequestInit,
  isRetry = false
): Promise<T> {
  // Proactively refresh if the token is about to expire (5 minute buffer)
  if (!path.startsWith('/auth/')) {
    const expiresAt = safeStorage.getItem('expires_at');
    if (expiresAt && Date.now() / 1000 > parseInt(expiresAt, 10) - 300) {
      await refreshTokenIfNeeded();
    }
  }

  const token = safeStorage.getItem('access_token');

  // A multipart body carries its own Content-Type, and only the browser can
  // write it: the `boundary` parameter has to match the delimiter it generates
  // while serializing the FormData. Setting the header by hand produces a
  // boundary-less type, and the server then reads the whole body as one opaque
  // blob and fails to find any part in it.
  const isMultipart = options?.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // Only set Content-Type for requests with a body the browser won't type itself
  if (options?.body && !isMultipart) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    // On 401, try to refresh the token and retry once. Safe for a multipart
    // body too: fetch re-serializes the FormData on each call rather than
    // consuming it. This is the *only* retry — a request that fails partway
    // through the network has an outcome we can't know, so it is never resent.
    if (response.status === 401 && !isRetry && !path.startsWith('/auth/')) {
      const refreshed = await refreshTokenIfNeeded();
      if (refreshed) {
        return request<T>(path, options, true);
      }
    }

    const fallback = isMultipart ? 'Upload failed' : 'Request failed';
    const error = await response.json().catch(() => ({ error: fallback }));
    throw new ApiError(response.status, error.error || fallback, error.code, error);
  }

  // 204/205 carry no body by definition. Parsing one throws, which would turn a
  // successful call into an error for any caller typed `void`.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  return response.json();
}

/**
 * A multipart POST. Goes through `request` like everything else, so an upload
 * started on a stale access token refreshes and retries instead of failing:
 * `request` leaves the Content-Type off once it sees a FormData body.
 */
function uploadForm<T>(path: string, form: FormData): Promise<T> {
  return request<T>(path, { method: 'POST', body: form });
}

export const api = {
  // Auth
  signIn: (data: SignInDTO) =>
    request<AuthResponse>('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  signUp: (data: SignUpDTO) =>
    request<AuthResponse>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  signOut: () =>
    request<{ success: boolean }>('/auth/signout', {
      method: 'POST',
    }),

  getMe: () =>
    request<{ user: { id: string; email: string; role: UserRole; fullName?: string } }>('/auth/me'),

  refreshToken: (refreshToken: string) =>
    request<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),

  forgotPassword: (email: string) =>
    request<{ success: boolean; message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (password: string) =>
    request<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // Users
  getProfile: () =>
    request<{ id: string; email: string; full_name?: string; phone?: string }>('/users/me'),

  updateProfile: (data: { full_name?: string; phone?: string }) =>
    request<{ id: string; email: string; full_name?: string; phone?: string }>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Trips
  getTrips: () =>
    request<Array<{
      id: string;
      name: string;
      destination: string;
      currency?: string;
      start_date?: string;
      end_date?: string;
      itinerary_image_url?: string;
    }>>('/trips'),

  getTrip: (id: string) =>
    request<{
      id: string;
      name: string;
      destination: string;
      currency?: string;
      start_date?: string;
      end_date?: string;
      itinerary_image_url?: string;
      stage?: string;
      tags?: string[];
      items: Array<{
        id: string;
        category: string;
        status: string;
        title: string;
        date: string;
        time?: string;
        details: Record<string, unknown>;
        confirmation?: string;
        cost?: number;
        paid?: number;
        currency?: string;
        booking_deadline?: string;
        cancellation_deadline?: string;
        final_payment_due?: string;
        supplier_name?: string;
        previous_item_id?: string;
      }>;
    }>(`/trips/${id}`),

  createTrip: (data: {
    name: string;
    destination: string;
    currency?: string;
    start_date?: string;
    end_date?: string;
    /** Seed the trip from a route. Needs `start_date`: a template is date-free. */
    template_id?: string;
    template_variant_id?: string;
  }) =>
    request<{ id: string }>('/trips', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTrip: (id: string, data: {
    name?: string;
    destination?: string;
    currency?: string;
    start_date?: string;
    end_date?: string;
    stage?: string;
    tags?: string[];
  }) =>
    request<{ id: string }>(`/trips/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteTrip: (id: string) =>
    request<{ success: boolean }>(`/trips/${id}`, {
      method: 'DELETE',
    }),

  // Trip Items
  createTripItem: (tripId: string, data: {
    category: string;
    status?: string;
    title: string;
    date: string;
    time?: string;
    endDate?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    previous_item_id?: string;
  }) =>
    request<{ id: string }>(`/trips/${tripId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTripItem: (tripId: string, itemId: string, data: {
    category?: string;
    status?: string;
    title?: string;
    date?: string;
    time?: string;
    endDate?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    previous_item_id?: string | null;
  }) =>
    request<{ id: string }>(`/trips/${tripId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteTripItem: (tripId: string, itemId: string) =>
    request<{ success: boolean }>(`/trips/${tripId}/items/${itemId}`, {
      method: 'DELETE',
    }),

  // Cruises
  createCruise: (tripId: string, data: {
    title: string;
    date: string;
    endDate: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    ports: Array<{
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
  }) =>
    request<{
      id: string;
      ports: Array<{ id: string }>;
    }>(`/trips/${tripId}/cruises`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCruise: (tripId: string, cruiseId: string, data: {
    title?: string;
    date?: string;
    endDate?: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    ports?: Array<{
      id?: string;
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
  }) =>
    request<{
      id: string;
      ports: Array<{ id: string }>;
    }>(`/trips/${tripId}/cruises/${cruiseId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getCruise: (tripId: string, cruiseId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      ports: Array<{
        id: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endTime?: string;
        details: Record<string, unknown>;
        day_offset?: number;
      }>;
    }>(`/trips/${tripId}/cruises/${cruiseId}`),

  // Multi-leg Flights
  createFlight: (tripId: string, data: {
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    details?: Record<string, unknown>;
    segments: Array<{
      departure_airport: string;
      departure_city: string;
      arrival_airport: string;
      arrival_city: string;
      departure_date: string;
      departure_time?: string;
      arrival_date?: string;
      arrival_time?: string;
      airline?: string;
      flight_number?: string;
      class?: string;
      seats?: string;
      aircraft?: string;
      leg: number;
      leg_label?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
  }) =>
    request<{
      id: string;
      segments: Array<{ id: string }>;
    }>(`/trips/${tripId}/flights`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFlight: (tripId: string, flightId: string, data: {
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    details?: Record<string, unknown>;
    segments?: Array<{
      id?: string;
      departure_airport: string;
      departure_city: string;
      arrival_airport: string;
      arrival_city: string;
      departure_date: string;
      departure_time?: string;
      arrival_date?: string;
      arrival_time?: string;
      airline?: string;
      flight_number?: string;
      class?: string;
      seats?: string;
      aircraft?: string;
      leg: number;
      leg_label?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
  }) =>
    request<{
      id: string;
      segments: Array<{ id: string }>;
    }>(`/trips/${tripId}/flights/${flightId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getFlight: (tripId: string, flightId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      segments: Array<{
        id: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endDate?: string;
        endTime?: string;
        details: Record<string, unknown>;
      }>;
    }>(`/trips/${tripId}/flights/${flightId}`),

  // Invitations
  validateInvite: (token: string) =>
    request<ValidateInviteResponse>(`/invitations/validate/${token}`),

  getInvitations: () =>
    request<Invitation[]>('/invitations'),

  createInvitation: (data: CreateInvitationDTO) =>
    request<Invitation>('/invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resendInvitation: (id: string) =>
    request<{ success: boolean }>(`/invitations/${id}/resend`, {
      method: 'POST',
    }),

  deleteInvitation: (id: string) =>
    request<{ success: boolean }>(`/invitations/${id}`, {
      method: 'DELETE',
    }),

  // Admin
  getAdminDeadlines: (options?: { window_days?: number; exclude_demo?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.window_days !== undefined) params.set('window_days', String(options.window_days));
    if (options?.exclude_demo) params.set('exclude_demo', 'true');
    const query = params.toString();
    return request<AdminDeadlinesResponse>(query ? `/admin/deadlines?${query}` : '/admin/deadlines');
  },

  getAdminTrips: (options?: { status?: 'current_and_upcoming'; exclude_demo?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.exclude_demo) params.set('exclude_demo', 'true');
    const query = params.toString();
    return request<Array<{
      id: string;
      user_id: string;
      user_ids?: string[];
      users?: Array<{ id: string; email: string; full_name?: string }>;
      name: string;
      destination: string;
      start_date?: string;
      end_date?: string;
      itinerary_image_url?: string;
      client_viewed_since_update?: boolean;
      user?: {
        email?: string;
        full_name?: string;
      };
    }>>(query ? `/admin/trips?${query}` : '/admin/trips');
  },

  getAdminDashboard: (options?: { exclude_demo?: boolean }) => {
    const query = options?.exclude_demo ? '?exclude_demo=true' : '';
    return request<AdminDashboard>(`/admin/dashboard${query}`);
  },

  /**
   * The admin search palette. With a query, grouped hits across clients,
   * leads, trips, suppliers, destinations and posts (narrowed to `kinds` when
   * given); with none, the current travels the palette opens on.
   */
  adminSearch: (query: string, kinds?: readonly AdminSearchKind[], options?: { exclude_demo?: boolean }) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (kinds?.length) params.set('kinds', kinds.join(','));
    if (options?.exclude_demo) params.set('exclude_demo', 'true');
    const qs = params.toString();
    return request<AdminSearchResponse>(qs ? `/admin/search?${qs}` : '/admin/search');
  },

  searchUsers: (query: string) =>
    request<Array<{ id: string; email: string; full_name?: string; last_login_at?: string | null }>>(
      `/admin/users/search?q=${encodeURIComponent(query)}`
    ),

  // --- Task tracker (a lead's next action, trip work, anything typed in by hand) ---

  /**
   * One bucket of the task list, plus the open counts for every bucket and the
   * business day the server counted from.
   */
  getTasks: (query: TaskListQuery & { exclude_demo?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (query.bucket) params.set('bucket', query.bucket);
    if (query.source) params.set('source', query.source);
    if (query.trip_id) params.set('trip_id', query.trip_id);
    if (query.lead_id) params.set('lead_id', query.lead_id);
    if (query.client_id) params.set('client_id', query.client_id);
    if (query.about_client_id) params.set('about_client_id', query.about_client_id);
    if (query.q) params.set('q', query.q);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.exclude_demo) params.set('exclude_demo', 'true');
    const qs = params.toString();
    return request<TaskListResponse>(qs ? `/admin/tasks?${qs}` : '/admin/tasks');
  },

  createTask: (data: TaskInput) =>
    request<Task>('/admin/tasks', { method: 'POST', body: JSON.stringify(data) }),

  updateTask: (taskId: string, data: TaskUpdate) =>
    request<Task>(`/admin/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTask: (taskId: string) =>
    request<{ success: boolean }>(`/admin/tasks/${taskId}`, { method: 'DELETE' }),

  /**
   * Ticks a task off (or reopens it). For a lead's task the answer carries
   * `contact_stamp` when the lead was marked contacted, so the page can say so.
   */
  completeTask: (taskId: string, done: boolean) =>
    request<TaskCompleteResponse>(`/admin/tasks/${taskId}/complete`, { method: 'POST', body: JSON.stringify({ done }) }),

  /** Moves the due date. Pushing it later is counted and the first date is kept. */
  postponeTask: (taskId: string, to: string) =>
    request<Task>(`/admin/tasks/${taskId}/postpone`, { method: 'POST', body: JSON.stringify({ to }) }),

  /** Takes back the last contacted date a completed lead task set; the task stays done. */
  undoTaskContactStamp: (taskId: string) =>
    request<{ task: Task; lead_id: string; last_contacted_at: string | null }>(`/admin/tasks/${taskId}/undo-contact-stamp`, { method: 'POST' }),

  /**
   * A trip's Tasks tab: its tasks, open first then done, and what the
   * itinerary checklist would add. Reading it creates nothing.
   */
  getTripTasks: (tripId: string) => request<TripTasksResponse>(`/admin/trips/${tripId}/tasks`),

  /** Adds the checklist tasks in `candidates`, turns down the ones in `skip`, and answers with the tab afterwards. */
  generateTripTasks: (tripId: string, data: GenerateTripTasksRequest) =>
    request<TripTasksResponse>(`/admin/trips/${tripId}/tasks/generate`, { method: 'POST', body: JSON.stringify(data) }),

  /**
   * Starts an AI review of a trip. It runs in the background, so the answer is
   * the review with its run still `processing`; read it again with
   * `getTripTaskReview` until it is not.
   */
  startTripTaskReview: (tripId: string) => request<TripTaskReview>(`/admin/trips/${tripId}/tasks/suggest`, { method: 'POST' }),

  /** Asks about one run and answers the review as it stands. What the page polls while a review is running. */
  getTripTaskReview: (tripId: string, runId: string) => request<TripTaskReview>(`/admin/trips/${tripId}/tasks/suggestions/${runId}`),

  /** Accepts, dismisses or reopens suggestions. Accepting one makes a task. Answers with the tab afterwards. */
  reviewTripTaskSuggestions: (tripId: string, items: TaskSuggestionReviewItem[]) =>
    request<TripTasksResponse>(`/admin/trips/${tripId}/tasks/suggestions/review`, { method: 'POST', body: JSON.stringify({ items }) }),

  /** How each checklist rule has fared across every trip, the ones turned away most first. */
  getTaskRuleUsage: () => request<TaskRuleUsageResponse>('/admin/tasks/rules/usage'),

  /** Where the Todoist mirror stands: whether it runs here, when it last synced, what it last did. */
  getTodoistStatus: () => request<TodoistStatus>('/admin/tasks/todoist'),

  /**
   * Runs one sync and answers with the status afterwards. A sync that fails
   * still answers: the status carries what went wrong.
   */
  runTodoistSync: (mode: TodoistSyncMode) =>
    request<TodoistStatus>('/admin/tasks/todoist/sync', { method: 'POST', body: JSON.stringify({ mode }) }),

  // --- Social media portal (stories and posts: templates, inspiration and creation sessions) ---

  getSocialBrand: () => request<SocialBrandGuide>('/admin/social/brand'),

  getSocialTemplates: (options: { format?: SocialFormat; include_archived?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (options.format) params.set('format', options.format);
    if (options.include_archived) params.set('include_archived', 'true');
    const query = params.toString();
    return request<SocialTemplateListResponse>(query ? `/admin/social/templates?${query}` : '/admin/social/templates').then((r) => r.templates);
  },

  createSocialTemplate: (data: CreateSocialTemplateDTO) =>
    request<SocialTemplate>('/admin/social/templates', { method: 'POST', body: JSON.stringify(data) }),

  getSocialTemplate: (templateId: string) => request<SocialTemplate>(`/admin/social/templates/${templateId}`),

  updateSocialTemplate: (templateId: string, data: UpdateSocialTemplateDTO) =>
    request<SocialTemplate>(`/admin/social/templates/${templateId}`, { method: 'PUT', body: JSON.stringify(data) }),

  /** Archives; templates are never deleted. */
  archiveSocialTemplate: (templateId: string) =>
    request<{ success: boolean }>(`/admin/social/templates/${templateId}`, { method: 'DELETE' }),

  uploadSocialTemplatePreview: (templateId: string, png: Blob) => {
    const form = new FormData();
    form.append('file', png, 'template-preview.png');
    return uploadForm<SocialTemplate>(`/admin/social/templates/${templateId}/preview`, form);
  },

  getSocialSessions: (options: SocialSessionListParams = {}) => {
    const params = new URLSearchParams();
    if (options.kind) params.set('kind', options.kind);
    if (options.include_archived) params.set('include_archived', 'true');
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<SocialSessionListResponse>(query ? `/admin/social/sessions?${query}` : '/admin/social/sessions');
  },

  createSocialSession: (data: CreateSocialSessionDTO) =>
    request<SocialSession>('/admin/social/sessions', { method: 'POST', body: JSON.stringify(data) }),

  /** Also settles any pending turn server-side. */
  getSocialSession: (sessionId: string) => request<SocialSession>(`/admin/social/sessions/${sessionId}`),

  updateSocialSession: (sessionId: string, data: UpdateSocialSessionDTO) =>
    request<SocialSession>(`/admin/social/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify(data) }),

  uploadSocialAsset: (sessionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return uploadForm<SocialAsset>(`/admin/social/sessions/${sessionId}/assets`, form);
  },

  /** 202: the persisted user turn and the pending assistant turn to poll. */
  createSocialMessage: (sessionId: string, data: CreateSocialMessageDTO) =>
    request<CreateSocialMessageResponse>(`/admin/social/sessions/${sessionId}/messages`, { method: 'POST', body: JSON.stringify(data) }),

  getSocialMessage: (sessionId: string, messageId: string) =>
    request<SocialMessageDetail>(`/admin/social/sessions/${sessionId}/messages/${messageId}`),

  retrySocialMessage: (sessionId: string, messageId: string) =>
    request<SocialMessage>(`/admin/social/sessions/${sessionId}/messages/${messageId}/retry`, { method: 'POST' }),

  getSocialRendering: (sessionId: string, renderingId: string) =>
    request<SocialRendering>(`/admin/social/sessions/${sessionId}/renderings/${renderingId}`),

  /** Keeps a browser-made PNG so the audit log has every export. */
  createSocialExport: (sessionId: string, renderingId: string, png: Blob, filename: string) => {
    const form = new FormData();
    form.append('file', png, filename);
    return uploadForm<SocialExport>(`/admin/social/sessions/${sessionId}/renderings/${renderingId}/exports`, form);
  },

  // --- Destinations (knowledge base: fact sheet, guide tips, places, sources) ---

  getKbDestinations: (options: KbDestinationListParams = {}) => {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.include_archived) params.set('include_archived', 'true');
    if (options.needs_review) params.set('needs_review', 'true');
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<KbDestinationListResponse>(query ? `/admin/destinations?${query}` : '/admin/destinations');
  },

  /** Typeahead over destinations; returns the rows only. */
  searchKbDestinations: (query: string): Promise<KbDestinationSummary[]> =>
    api.getKbDestinations({ q: query, limit: 10 }).then((r) => r.destinations),

  /** Resolves a trip's free-text destination to a guide, when one matches. */
  matchKbDestination: (q: string) =>
    request<KbMatchResponse>(`/admin/destinations/match?q=${encodeURIComponent(q)}`),

  /** Several place names in one request, answered under each name as sent. */
  matchKbDestinations: (queries: string[]) =>
    request<KbMatchManyResponse>('/admin/destinations/match', { method: 'POST', body: JSON.stringify({ queries }) }),

  /** Does Wikivoyage have a page for this place? Asked before a destination is created. */
  lookupKbWikivoyage: (q: string) =>
    request<KbWikivoyageLookup>(`/admin/destinations/wikivoyage?q=${encodeURIComponent(q)}`),

  createKbDestination: (data: CreateKbDestinationDTO) =>
    request<KbDestination>('/admin/destinations', { method: 'POST', body: JSON.stringify(data) }),

  getKbDestination: (destinationId: string) =>
    request<KbDestination>(`/admin/destinations/${destinationId}`),

  updateKbDestination: (destinationId: string, data: UpdateKbDestinationDTO) =>
    request<KbDestination>(`/admin/destinations/${destinationId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteKbDestination: (destinationId: string) =>
    request<{ success: boolean }>(`/admin/destinations/${destinationId}`, { method: 'DELETE' }),

  /** The places of each direct child, for a region or country page. */
  getKbRollup: (destinationId: string) =>
    request<KbPlacesRollup>(`/admin/destinations/${destinationId}/rollup`),

  /** Blog posts linked to this destination, newest edit first. */
  getKbDestinationPosts: (destinationId: string) =>
    request<DestinationBlogPost[]>(`/admin/destinations/${destinationId}/posts`),

  /** Everything an ingestion run left waiting for review. */
  getKbProposed: (destinationId: string) =>
    request<KbProposed>(`/admin/destinations/${destinationId}/proposed`),

  reviewKb: (destinationId: string, items: KbReviewItem[]) =>
    request<KbReviewResponse>(`/admin/destinations/${destinationId}/review`, {
      method: 'POST',
      body: JSON.stringify({ items } satisfies KbReviewRequest),
    }),

  createKbFact: (destinationId: string, data: KbFactInput) =>
    request<KbFact>(`/admin/destinations/${destinationId}/facts`, { method: 'POST', body: JSON.stringify(data) }),

  updateKbFact: (destinationId: string, factId: string, data: Partial<KbFactInput>) =>
    request<KbFact>(`/admin/destinations/${destinationId}/facts/${factId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteKbFact: (destinationId: string, factId: string) =>
    request<{ success: boolean }>(`/admin/destinations/${destinationId}/facts/${factId}`, { method: 'DELETE' }),

  createKbEntry: (destinationId: string, data: KbEntryInput) =>
    request<KbEntry>(`/admin/destinations/${destinationId}/entries`, { method: 'POST', body: JSON.stringify(data) }),

  updateKbEntry: (destinationId: string, entryId: string, data: Partial<KbEntryInput>) =>
    request<KbEntry>(`/admin/destinations/${destinationId}/entries/${entryId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteKbEntry: (destinationId: string, entryId: string) =>
    request<{ success: boolean }>(`/admin/destinations/${destinationId}/entries/${entryId}`, { method: 'DELETE' }),

  createKbTip: (destinationId: string, data: KbTipInput) =>
    request<KbTip>(`/admin/destinations/${destinationId}/tips`, { method: 'POST', body: JSON.stringify(data) }),

  updateKbTip: (destinationId: string, tipId: string, data: Partial<KbTipInput>) =>
    request<KbTip>(`/admin/destinations/${destinationId}/tips/${tipId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteKbTip: (destinationId: string, tipId: string) =>
    request<{ success: boolean }>(`/admin/destinations/${destinationId}/tips/${tipId}`, { method: 'DELETE' }),

  /** Starts an extraction from pasted text or a Wikivoyage URL; poll `getKbSource` until it settles. */
  createKbSource: (destinationId: string, data: CreateKbSourceDTO) =>
    request<KbSource>(`/admin/destinations/${destinationId}/sources`, { method: 'POST', body: JSON.stringify(data) }),

  /** Same as `createKbSource` for a PDF: `file` plus `title`, `attribution`, `look_for`, `instructions` fields. */
  uploadKbSourcePdf: (destinationId: string, formData: FormData) =>
    uploadForm<KbSource>(`/admin/destinations/${destinationId}/sources`, formData),

  getKbSources: (destinationId: string) =>
    request<KbSource[]>(`/admin/destinations/${destinationId}/sources`),

  getKbSource: (destinationId: string, sourceId: string) =>
    request<KbSource>(`/admin/destinations/${destinationId}/sources/${sourceId}`),

  retryKbSource: (destinationId: string, sourceId: string) =>
    request<KbSource>(`/admin/destinations/${destinationId}/sources/${sourceId}/retry`, { method: 'POST' }),

  deleteKbSource: (destinationId: string, sourceId: string) =>
    request<{ success: boolean }>(`/admin/destinations/${destinationId}/sources/${sourceId}`, { method: 'DELETE' }),

  // --- Suppliers (the trade: organizations, the people in them, what was said) ---

  getSupplierDirectory: (options: SupplierDirectoryParams = {}) => {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.destination_id) params.set('destination_id', options.destination_id);
    if (options.type) params.set('type', options.type);
    if (options.specialty) params.set('specialty', options.specialty);
    if (options.relationship_status) params.set('relationship_status', options.relationship_status);
    if (options.include_archived) params.set('include_archived', 'true');
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<SupplierDirectoryResponse>(query ? `/admin/suppliers?${query}` : '/admin/suppliers');
  },

  /** Typeahead over supplier organizations, for the pickers on a contact or a connection. */
  searchSupplierOrganizations: (query: string) =>
    api.getSupplierDirectory({ q: query, limit: 10 }).then((r) => r.organizations),

  searchSupplierContacts: (query: string) =>
    api.getSupplierDirectory({ q: query, limit: 10 }).then((r) => r.contacts),

  createSupplierOrganization: (data: SupplierOrganizationInput) =>
    request<SupplierOrganization>('/admin/suppliers/organizations', { method: 'POST', body: JSON.stringify(data) }),

  getSupplierOrganization: (organizationId: string) =>
    request<SupplierOrganization>(`/admin/suppliers/organizations/${organizationId}`),

  updateSupplierOrganization: (organizationId: string, data: Partial<SupplierOrganizationInput>) =>
    request<SupplierOrganization>(`/admin/suppliers/organizations/${organizationId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSupplierOrganization: (organizationId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/organizations/${organizationId}`, { method: 'DELETE' }),

  createSupplierOrganizationLink: (organizationId: string, data: SupplierOrganizationLinkInput) =>
    request<SupplierOrganizationLink>(`/admin/suppliers/organizations/${organizationId}/links`, { method: 'POST', body: JSON.stringify(data) }),

  deleteSupplierOrganizationLink: (organizationId: string, linkId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/organizations/${organizationId}/links/${linkId}`, { method: 'DELETE' }),

  createSupplierContact: (data: SupplierContactInput) =>
    request<SupplierContact>('/admin/suppliers/contacts', { method: 'POST', body: JSON.stringify(data) }),

  getSupplierContact: (contactId: string) => request<SupplierContact>(`/admin/suppliers/contacts/${contactId}`),

  updateSupplierContact: (contactId: string, data: Partial<SupplierContactInput>) =>
    request<SupplierContact>(`/admin/suppliers/contacts/${contactId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSupplierContact: (contactId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/contacts/${contactId}`, { method: 'DELETE' }),

  createSupplierAffiliation: (contactId: string, data: SupplierAffiliationInput) =>
    request<SupplierAffiliation>(`/admin/suppliers/contacts/${contactId}/affiliations`, { method: 'POST', body: JSON.stringify(data) }),

  updateSupplierAffiliation: (contactId: string, affiliationId: string, data: Partial<SupplierAffiliationInput>) =>
    request<SupplierAffiliation>(`/admin/suppliers/contacts/${contactId}/affiliations/${affiliationId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSupplierAffiliation: (contactId: string, affiliationId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/contacts/${contactId}/affiliations/${affiliationId}`, { method: 'DELETE' }),

  createSupplierIntroduction: (contactId: string, data: SupplierIntroductionInput) =>
    request<SupplierIntroduction>(`/admin/suppliers/contacts/${contactId}/introductions`, { method: 'POST', body: JSON.stringify(data) }),

  deleteSupplierIntroduction: (contactId: string, introductionId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/contacts/${contactId}/introductions/${introductionId}`, { method: 'DELETE' }),

  createSupplierInteraction: (data: SupplierInteractionInput) =>
    request<SupplierInteraction>('/admin/suppliers/interactions', { method: 'POST', body: JSON.stringify(data) }),

  updateSupplierInteraction: (interactionId: string, data: Partial<SupplierInteractionInput>) =>
    request<SupplierInteraction>(`/admin/suppliers/interactions/${interactionId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSupplierInteraction: (interactionId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/interactions/${interactionId}`, { method: 'DELETE' }),

  getSupplierFollowUps: (filter: SupplierFollowUpFilter = 'open') =>
    request<SupplierFollowUpListResponse>(`/admin/suppliers/follow-ups?filter=${filter}`),

  createSupplierFollowUp: (data: SupplierFollowUpInput) =>
    request<SupplierFollowUp>('/admin/suppliers/follow-ups', { method: 'POST', body: JSON.stringify(data) }),

  updateSupplierFollowUp: (followUpId: string, data: Partial<SupplierFollowUpInput>) =>
    request<SupplierFollowUp>(`/admin/suppliers/follow-ups/${followUpId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteSupplierFollowUp: (followUpId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/follow-ups/${followUpId}`, { method: 'DELETE' }),

  getSupplierCaptures: (options: SupplierCaptureListParams = {}) => {
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<SupplierCaptureListResponse>(query ? `/admin/suppliers/captures?${query}` : '/admin/suppliers/captures');
  },

  /** Pasted text only; files go through `uploadSupplierCapture`. */
  createSupplierCapture: (data: CreateSupplierCaptureDTO) =>
    request<SupplierCapture>('/admin/suppliers/captures', { method: 'POST', body: JSON.stringify(data) }),

  /** `file` repeated per document, plus `title`, `event_name`, `event_date`, `note`, `text`. */
  uploadSupplierCapture: (form: FormData) => uploadForm<SupplierCapture>('/admin/suppliers/captures', form),

  getSupplierCapture: (captureId: string) => request<SupplierCapture>(`/admin/suppliers/captures/${captureId}`),

  retrySupplierCapture: (captureId: string) =>
    request<SupplierCapture>(`/admin/suppliers/captures/${captureId}/retry`, { method: 'POST' }),

  deleteSupplierCapture: (captureId: string) =>
    request<{ success: boolean }>(`/admin/suppliers/captures/${captureId}`, { method: 'DELETE' }),

  getSupplierProposals: (captureId: string) => request<SupplierProposalQueue>(`/admin/suppliers/captures/${captureId}/proposals`),

  reviewSupplierCapture: (captureId: string, items: SupplierReviewItem[]) =>
    request<SupplierReviewResponse>(`/admin/suppliers/captures/${captureId}/review`, {
      method: 'POST',
      body: JSON.stringify({ items } satisfies SupplierReviewRequest),
    }),

  // --- Clients (CRM records; a client may or may not have a login) ---

  getClients: (options: ClientListParams = {}) => {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.type) params.set('type', options.type);
    if (options.has_account !== undefined) params.set('has_account', String(options.has_account));
    if (options.sort) params.set('sort', options.sort);
    if (options.exclude_demo) params.set('exclude_demo', 'true');
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<ClientListResponse>(query ? `/admin/clients?${query}` : '/admin/clients');
  },

  /** Typeahead over everyone (clients and travelers); returns the rows only. */
  searchClients: (query: string): Promise<ClientSummary[]> =>
    api.getClients({ q: query, type: 'ALL', limit: 10 }).then((r) => r.clients),

  createClient: (data: CreateClientDTO) =>
    request<Client>('/admin/clients', { method: 'POST', body: JSON.stringify(data) }),

  getClient: (clientId: string) => request<Client>(`/admin/clients/${clientId}`),

  updateClient: (clientId: string, data: UpdateClientDTO) =>
    request<Client>(`/admin/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteClient: (clientId: string) =>
    request<{ success: boolean }>(`/admin/clients/${clientId}`, { method: 'DELETE' }),

  /** Resolves an account (profile) id to its client record; used to redirect old links. */
  getClientByProfile: (userId: string) =>
    request<ClientByProfileResponse>(`/admin/clients/by-profile/${userId}`),

  addClientRelationship: (clientId: string, data: CreateClientRelationshipDTO) =>
    request<ClientRelationship>(`/admin/clients/${clientId}/relationships`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeClientRelationship: (clientId: string, relationshipId: string) =>
    request<{ success: boolean }>(`/admin/clients/${clientId}/relationships/${relationshipId}`, {
      method: 'DELETE',
    }),

  getClientLeads: (clientId: string) =>
    request<ClientLeadSummary[]>(`/admin/clients/${clientId}/leads`),

  getClientTrips: (clientId: string) =>
    request<ClientTrip[]>(`/admin/clients/${clientId}/trips`),

  getClientNotes: (clientId: string) =>
    request<ClientNote[]>(`/admin/clients/${clientId}/notes`),

  createClientNote: (clientId: string, data: CreateClientNoteDTO) =>
    request<ClientNote>(`/admin/clients/${clientId}/notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateClientNote: (clientId: string, noteId: string, data: UpdateClientNoteDTO) =>
    request<ClientNote>(`/admin/clients/${clientId}/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteClientNote: (clientId: string, noteId: string) =>
    request<{ success: boolean }>(`/admin/clients/${clientId}/notes/${noteId}`, {
      method: 'DELETE',
    }),

  getUser: (userId: string) =>
    request<{ id: string; email: string; full_name?: string; last_login_at?: string | null }>(
      `/admin/users/${userId}`
    ),

  getTravelProfile: (userId: string) =>
    request<TravelProfile>(`/admin/users/${userId}/travel-profile`),

  updateTravelProfile: (userId: string, data: UpdateTravelProfileDTO) =>
    request<TravelProfile>(`/admin/users/${userId}/travel-profile`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getUserTrips: (userId: string) =>
    request<Array<{
      id: string;
      user_id: string;
      name: string;
      destination: string;
      start_date?: string;
      end_date?: string;
    }>>(`/admin/users/${userId}/trips`),

  createTripForUser: (userId: string, data: {
    name: string;
    destination: string;
    start_date?: string;
    end_date?: string;
  }) =>
    request<{ id: string }>(`/admin/users/${userId}/trips`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAdminTrip: (tripId: string) =>
    request<{
      id: string;
      user_id: string;
      user_ids?: string[];
      users?: Array<{ id: string; email: string; full_name?: string }>;
      name: string;
      destination: string;
      currency?: string;
      start_date?: string;
      end_date?: string;
      itinerary_image_url?: string;
      stage?: string;
      tags?: string[];
      items: Array<{
        id: string;
        category: string;
        status: string;
        title: string;
        date: string;
        time?: string;
        endDate?: string;
        endTime?: string;
        details: Record<string, unknown>;
        confirmation?: string;
        cost?: number;
        paid?: number;
        currency?: string;
        booking_deadline?: string;
        cancellation_deadline?: string;
        final_payment_due?: string;
        supplier_name?: string;
        previous_item_id?: string;
      }>;
    }>(`/admin/trips/${tripId}`),

  getAdminAnalyticsViews: (options?: { limit?: number; offset?: number; excludeAdmins?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.excludeAdmins) params.set('excludeAdmins', 'true');
    const query = params.toString();
    return request<{
      views: Array<{
        id: string;
        viewed_at: string;
        user: { id: string; email: string; full_name: string | null };
        trip: { id: string; name: string; destination: string };
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(query ? `/admin/analytics/views?${query}` : '/admin/analytics/views');
  },

  getAdminTripViews: (tripId: string) =>
    request<{
      views: Array<{
        id: string;
        user_id: string;
        email: string;
        full_name: string | null;
        viewed_at: string;
      }>;
      user_summary: Array<{
        user_id: string;
        email: string;
        full_name: string | null;
        view_count: number;
        first_viewed: string;
        last_viewed: string;
      }>;
      total_views: number;
    }>(`/admin/trips/${tripId}/views`),

  updateAdminTrip: (tripId: string, data: {
    name?: string;
    destination?: string;
    currency?: string;
    start_date?: string;
    end_date?: string;
    user_ids?: string[];
    stage?: string;
    tags?: string[];
  }) =>
    request<{ id: string }>(`/admin/trips/${tripId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  createTripForUsers: (userIds: string[], data: {
    name: string;
    destination: string;
    currency?: string;
    start_date?: string;
    end_date?: string;
    /** Seed the trip from a route. Needs `start_date`: a template is date-free. */
    template_id?: string;
    template_variant_id?: string;
  }) =>
    request<{ id: string }>('/admin/trips', {
      method: 'POST',
      body: JSON.stringify({ ...data, user_ids: userIds }),
    }),

  deleteAdminTrip: (tripId: string) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}`, {
      method: 'DELETE',
    }),

  duplicateAdminTrip: (tripId: string, userIds: string[], newStartDate?: string) =>
    request<{
      id: string;
      user_id: string;
      user_ids?: string[];
      users?: Array<{ id: string; email: string; full_name?: string }>;
      name: string;
      destination: string;
      currency?: string;
      start_date?: string;
      end_date?: string;
    }>(`/admin/trips/${tripId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds,
        ...(newStartDate ? { new_start_date: newStartDate } : {}),
      }),
    }),

  createAdminTripItem: (tripId: string, data: {
    category: string;
    status?: string;
    title: string;
    date: string;
    time?: string;
    endDate?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    previous_item_id?: string;
    traveler_ids?: string[];
  }) =>
    request<{ id: string }>(`/admin/trips/${tripId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  parseBulkIdeas: (tripId: string, text: string) =>
    request<{
      items: Array<{
        source: string;
        query: string;
        match: {
          place_id: string;
          name: string;
          formatted_address: string;
          lat?: number;
          lng?: number;
          photo_reference?: string;
          photo_references: string[];
          website?: string;
          phone?: string;
        } | null;
        error?: string;
      }>;
      truncated: boolean;
    }>(`/admin/trips/${tripId}/bulk-ideas/parse`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  // Extract a trip-item candidate from a booking confirmation (parse-only preview;
  // nothing is saved server-side)
  extractItemFromFile: (tripId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadForm<{ item: ExtractedItemCandidate }>(
      `/admin/trips/${tripId}/items/extract`,
      formData,
    );
  },

  extractItemFromText: (tripId: string, text: string) =>
    request<{ item: ExtractedItemCandidate }>(`/admin/trips/${tripId}/items/extract`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  updateAdminTripItem: (tripId: string, itemId: string, data: {
    category?: string;
    status?: string;
    title?: string;
    date?: string;
    time?: string;
    endDate?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    previous_item_id?: string | null;
    traveler_ids?: string[];
  }) =>
    request<{ id: string }>(`/admin/trips/${tripId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteAdminTripItem: (tripId: string, itemId: string) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}/items/${itemId}`, {
      method: 'DELETE',
    }),

  duplicateAdminTripItem: (sourceTripId: string, itemId: string, targetTripId: string) =>
    request<{ id: string }>(`/admin/trips/${sourceTripId}/items/${itemId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ target_trip_id: targetTripId }),
    }),

  convertIdeaToItem: (tripId: string, itemId: string, data: {
    target_category: 'activity' | 'reservation' | 'note';
    date: string;
    time?: string;
  }) =>
    request<{
      id: string;
      category: string;
      title: string;
      date: string;
      time?: string;
      details: Record<string, unknown>;
    }>(`/admin/trips/${tripId}/items/${itemId}/convert`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Create activity from idea (keeps idea intact, for itinerary builder)
  createActivityFromIdea: (tripId: string, ideaId: string, data: {
    date: string;
    time?: string;
  }) =>
    request<{
      id: string;
      category: string;
      status: string;
      title: string;
      date: string;
      time?: string;
      details: Record<string, unknown>;
    }>(`/admin/trips/${tripId}/items/from-idea/${ideaId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Admin Cruises
  createAdminCruise: (tripId: string, data: {
    title: string;
    date: string;
    endDate: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    ports: Array<{
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      ports: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/cruises`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminCruise: (tripId: string, cruiseId: string, data: {
    title?: string;
    date?: string;
    endDate?: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    ports?: Array<{
      id?: string;
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      ports: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/cruises/${cruiseId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminCruise: (tripId: string, cruiseId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      ports: Array<{
        id: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endTime?: string;
        details: Record<string, unknown>;
        day_offset?: number;
      }>;
    }>(`/admin/trips/${tripId}/cruises/${cruiseId}`),

  // Admin Roadtrips
  createAdminRoadtrip: (tripId: string, data: {
    title: string;
    date: string;
    endDate: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    stops: Array<{
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      stops: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/roadtrips`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminRoadtrip: (tripId: string, roadtripId: string, data: {
    title?: string;
    date?: string;
    endDate?: string;
    time?: string;
    endTime?: string;
    details?: Record<string, unknown>;
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    stops?: Array<{
      id?: string;
      title: string;
      day_offset: number;
      time?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      stops: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/roadtrips/${roadtripId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminRoadtrip: (tripId: string, roadtripId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      stops: Array<{
        id: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endTime?: string;
        details: Record<string, unknown>;
        day_offset?: number;
      }>;
    }>(`/admin/trips/${tripId}/roadtrips/${roadtripId}`),

  // Admin Multi-leg Flights
  createAdminFlight: (tripId: string, data: {
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string;
    booking_deadline?: string;
    cancellation_deadline?: string;
    final_payment_due?: string;
    supplier_name?: string;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    details?: Record<string, unknown>;
    segments: Array<{
      departure_airport: string;
      departure_city: string;
      arrival_airport: string;
      arrival_city: string;
      departure_date: string;
      departure_time?: string;
      arrival_date?: string;
      arrival_time?: string;
      airline?: string;
      flight_number?: string;
      class?: string;
      seats?: string;
      aircraft?: string;
      leg: number;
      leg_label?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      segments: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/flights`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminFlight: (tripId: string, flightId: string, data: {
    confirmation?: string;
    cost?: number;
    paid?: number;
    currency?: string | null;
    booking_deadline?: string | null;
    cancellation_deadline?: string | null;
    final_payment_due?: string | null;
    supplier_name?: string | null;
    status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    details?: Record<string, unknown>;
    segments?: Array<{
      id?: string;
      departure_airport: string;
      departure_city: string;
      arrival_airport: string;
      arrival_city: string;
      departure_date: string;
      departure_time?: string;
      arrival_date?: string;
      arrival_time?: string;
      airline?: string;
      flight_number?: string;
      class?: string;
      seats?: string;
      aircraft?: string;
      leg: number;
      leg_label?: string;
      details?: Record<string, unknown>;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      segments: Array<{ id: string }>;
    }>(`/admin/trips/${tripId}/flights/${flightId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminFlight: (tripId: string, flightId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      segments: Array<{
        id: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endDate?: string;
        endTime?: string;
        details: Record<string, unknown>;
      }>;
    }>(`/admin/trips/${tripId}/flights/${flightId}`),

  getItineraryCities: (tripId: string) =>
    request<{ cities: string[] }>(`/admin/trips/${tripId}/itinerary-cities`),

  getItineraryImagePrompt: (tripId: string) =>
    request<{ prompt: string }>(
      `/admin/trips/${tripId}/image-prompt`,
      {
        method: 'POST',
      }
    ),

  generateItineraryImage: (tripId: string, prompt?: string) =>
    request<{ success: boolean; itinerary_image_url: string }>(
      `/admin/trips/${tripId}/generate-image`,
      {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      }
    ),

  clearItineraryImage: (tripId: string) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}/image`, {
      method: 'DELETE',
    }),

  // Route map: the drawn map that replaces the painted itinerary image
  getRouteMap: (tripId: string) => request<TripRouteMapResponse>(`/admin/trips/${tripId}/route-map`),

  /** Reads the trip, asks the model what goes on the map and looks the places up. About a minute. */
  buildRouteMap: (tripId: string) => request<TripRouteMapResponse>(`/admin/trips/${tripId}/route-map`, { method: 'POST' }),

  updateRouteMap: (tripId: string, body: { spec?: RouteMapSpec; theme?: RouteMapThemeName }) =>
    request<TripRouteMapResponse>(`/admin/trips/${tripId}/route-map`, { method: 'PUT', body: JSON.stringify(body) }),

  /** Both themes' PNGs, drawn in the browser; the one for `theme` becomes the trip's itinerary image. */
  uploadRouteMapImages: (tripId: string, images: Record<RouteMapThemeName, Blob>, theme: RouteMapThemeName) => {
    const form = new FormData();
    for (const [name, blob] of Object.entries(images)) form.append(name, blob, `route-map-${name}.png`);
    form.append('theme', theme);
    return uploadForm<TripRouteMapResponse>(`/admin/trips/${tripId}/route-map/images`, form);
  },

  // Countries
  getCountries: () =>
    request<Country[]>('/countries'),

  searchCountries: (query: string) =>
    request<Country[]>(`/countries/search?q=${encodeURIComponent(query)}`),

  getEmergencyNumbers: (countryCodes: string[]) =>
    request<Country[]>(`/countries/emergency?codes=${countryCodes.join(',')}`),

  // Exchange rates
  getExchangeRates: (base: string, symbols: string[]) =>
    request<{
      base: string;
      as_of: string;
      rates: Record<string, number>;
    }>(
      `/exchange-rates/latest?base=${encodeURIComponent(base)}&symbols=${encodeURIComponent(symbols.join(','))}`
    ),

  // Airports
  searchAirports: (query: string) =>
    request<Array<{ iata: string; name: string; city: string; country: string }>>(
      `/airports/search?q=${encodeURIComponent(query)}`
    ),

  // Airlines
  searchAirlines: (query: string) =>
    request<Array<{ iata: string; name: string; country: string }>>(
      `/flight-data/airlines/search?q=${encodeURIComponent(query)}`
    ),

  // Aircraft
  searchAircraft: (query: string) =>
    request<Array<{ icao: string; name: string; manufacturer: string }>>(
      `/flight-data/aircraft/search?q=${encodeURIComponent(query)}`
    ),

  // Places (Google Places API)
  searchPlaces: (query: string, options?: { lat?: number; lng?: number; types?: string }) => {
    const params = new URLSearchParams({ q: query });
    if (options?.lat && options?.lng) {
      params.set('lat', String(options.lat));
      params.set('lng', String(options.lng));
    }
    if (options?.types) {
      params.set('types', options.types);
    }
    return request<Array<{
      place_id: string;
      name: string;
      formatted_address: string;
      types: string[];
      photo_reference?: string;
    }>>(`/places/search?${params}`);
  },

  getPlaceDetails: (placeId: string) =>
    request<{
      place_id: string;
      name: string;
      formatted_address: string;
      formatted_phone_number?: string;
      international_phone_number?: string;
      website?: string;
      url?: string;
      types: string[];
      lat?: number;
      lng?: number;
      country_code?: string;
      photos?: Array<{ name: string; widthPx: number; heightPx: number }>;
    }>(`/places/details/${placeId}`),

  searchNearby: (location: { lat: number; lng: number }, options?: { types?: string[]; radius?: number }) => {
    const params = new URLSearchParams({
      lat: String(location.lat),
      lng: String(location.lng),
    });
    if (options?.types && options.types.length > 0) {
      params.set('types', options.types.join(','));
    }
    if (options?.radius) {
      params.set('radius', String(options.radius));
    }
    return request<Array<{
      place_id: string;
      name: string;
      formatted_address: string;
      types: string[];
      photo_reference?: string;
    }>>(`/places/nearby?${params}`);
  },

  geocodeLocation: (address: string) =>
    request<{ lat: number; lng: number }>(`/places/geocode?address=${encodeURIComponent(address)}`),

  // User Choice Sets
  getChoiceSet: (tripId: string, choiceId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      endDate?: string;
      details: Record<string, unknown>;
      options: Array<{
        id: string;
        category: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endDate?: string;
        endTime?: string;
        details: Record<string, unknown>;
        confirmation?: string;
        cost?: number;
        paid?: number;
        currency?: string;
        booking_deadline?: string;
        cancellation_deadline?: string;
        final_payment_due?: string;
        supplier_name?: string;
        recommended_by_admin: boolean;
        preferences: Array<{
          user_id: string;
          user_email: string;
          user_full_name?: string;
          created_at: string;
        }>;
        preference_count: number;
        segments?: Array<{
          id: string;
          title: string;
          date: string;
          time?: string;
          endDate?: string;
          endTime?: string;
          details: Record<string, unknown>;
        }>;
      }>;
      is_committed: boolean;
      is_closed: boolean;
      closed_reason?: string;
      max_preferences: number;
    }>(`/trips/${tripId}/choices/${choiceId}`),

  setPreference: (tripId: string, choiceId: string, optionId: string) =>
    request<ChoiceSetWithOptions>(`/trips/${tripId}/choices/${choiceId}/preference`, {
      method: 'POST',
      body: JSON.stringify({ option_id: optionId }),
    }),

  removePreference: (tripId: string, choiceId: string, optionId: string) =>
    request<ChoiceSetWithOptions>(`/trips/${tripId}/choices/${choiceId}/preference/${optionId}`, {
      method: 'DELETE',
    }),

  // Admin Choice Sets
  createAdminChoiceSet: (tripId: string, data: {
    title: string;
    date: string;
    endDate?: string;
    details?: Record<string, unknown>;
    max_preferences?: number;
    options: Array<{
      category: string;
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      segments?: Array<{
        departure_airport: string;
        departure_city: string;
        arrival_airport: string;
        arrival_city: string;
        departure_date: string;
        departure_time?: string;
        arrival_date?: string;
        arrival_time?: string;
        airline?: string;
        flight_number?: string;
        class?: string;
        seats?: string;
        aircraft?: string;
        leg: number;
        leg_label?: string;
        details?: Record<string, unknown>;
        status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      }>;
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      options: Array<{ id: string; segments?: Array<{ id: string }> }>;
    }>(`/admin/trips/${tripId}/choices`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminChoiceSet: (tripId: string, choiceId: string, data: {
    title?: string;
    date?: string;
    endDate?: string;
    details?: Record<string, unknown>;
    max_preferences?: number;
    options?: Array<{
      id?: string;
      category: string;
      title: string;
      date: string;
      time?: string;
      endDate?: string;
      endTime?: string;
      details?: Record<string, unknown>;
      confirmation?: string;
      cost?: number;
      paid?: number;
      currency?: string;
      booking_deadline?: string;
      cancellation_deadline?: string;
      final_payment_due?: string;
      supplier_name?: string;
      status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      segments?: Array<{
        id?: string;
        departure_airport: string;
        departure_city: string;
        arrival_airport: string;
        arrival_city: string;
        departure_date: string;
        departure_time?: string;
        arrival_date?: string;
        arrival_time?: string;
        airline?: string;
        flight_number?: string;
        class?: string;
        seats?: string;
        aircraft?: string;
        leg: number;
        leg_label?: string;
        details?: Record<string, unknown>;
        status?: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      }>;
    }>;
    traveler_ids?: string[];
  }) =>
    request<{
      id: string;
      options: Array<{ id: string; segments?: Array<{ id: string }> }>;
    }>(`/admin/trips/${tripId}/choices/${choiceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAdminChoiceSet: (tripId: string, choiceId: string) =>
    request<{
      id: string;
      category: string;
      status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
      title: string;
      date: string;
      endDate?: string;
      details: Record<string, unknown>;
      options: Array<{
        id: string;
        category: string;
        status: 'proposed' | 'ready_to_book' | 'booked' | 'n_a';
        title: string;
        date: string;
        time?: string;
        endDate?: string;
        endTime?: string;
        details: Record<string, unknown>;
        confirmation?: string;
        cost?: number;
        paid?: number;
        currency?: string;
        booking_deadline?: string;
        cancellation_deadline?: string;
        final_payment_due?: string;
        supplier_name?: string;
        recommended_by_admin: boolean;
        preferences: Array<{
          user_id: string;
          user_email: string;
          user_full_name?: string;
          created_at: string;
        }>;
        preference_count: number;
        segments?: Array<{
          id: string;
          title: string;
          date: string;
          time?: string;
          endDate?: string;
          endTime?: string;
          details: Record<string, unknown>;
        }>;
      }>;
      is_committed: boolean;
      is_closed: boolean;
      closed_reason?: string;
      max_preferences: number;
      // Non-admin trip members the choice set was put to, for the vote tally
      eligible_travelers?: Array<{
        id: string;
        email: string;
        full_name?: string;
      }>;
    }>(`/admin/trips/${tripId}/choices/${choiceId}`),

  setOptionRecommendation: (tripId: string, choiceId: string, optionId: string, recommended: boolean) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}/choices/${choiceId}/recommend`, {
      method: 'POST',
      body: JSON.stringify({ option_id: optionId, recommended }),
    }),

  commitChoice: (tripId: string, choiceId: string, winningOptionId: string) =>
    request<{ success: boolean; winner_id: string }>(`/admin/trips/${tripId}/choices/${choiceId}/commit`, {
      method: 'POST',
      body: JSON.stringify({ winning_option_id: winningOptionId }),
    }),

  setChoiceSetClosed: (tripId: string, choiceId: string, closed: boolean, reason?: string) =>
    request<ChoiceSetWithOptions>(`/admin/trips/${tripId}/choices/${choiceId}/close`, {
      method: 'POST',
      body: JSON.stringify({ closed, ...(reason ? { reason } : {}) }),
    }),

  deleteAdminChoiceSet: (tripId: string, choiceId: string) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}/choices/${choiceId}`, {
      method: 'DELETE',
    }),

  // Trip Item Files
  uploadTripItemFiles: (tripId: string, itemId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return uploadForm<{ files: TripItemFile[] }>(
      `/trips/${tripId}/items/${itemId}/files`,
      formData,
    );
  },

  getTripItemFiles: (tripId: string, itemId: string) =>
    request<{ files: TripItemFile[] }>(`/trips/${tripId}/items/${itemId}/files`),

  getTripItemFileUrl: (tripId: string, itemId: string, fileId: string) =>
    request<{ url: string; expires_in: number }>(`/trips/${tripId}/items/${itemId}/files/${fileId}/url`),

  deleteTripItemFile: (tripId: string, itemId: string, fileId: string) =>
    request<{ success: boolean }>(`/trips/${tripId}/items/${itemId}/files/${fileId}`, {
      method: 'DELETE',
    }),

  // Admin Trip Item Files
  uploadAdminTripItemFiles: (tripId: string, itemId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return uploadForm<{ files: TripItemFile[] }>(
      `/admin/trips/${tripId}/items/${itemId}/files`,
      formData,
    );
  },

  getAdminTripItemFiles: (tripId: string, itemId: string) =>
    request<{ files: TripItemFile[] }>(`/admin/trips/${tripId}/items/${itemId}/files`),

  getAdminTripItemFileUrl: (tripId: string, itemId: string, fileId: string) =>
    request<{ url: string; expires_in: number }>(`/admin/trips/${tripId}/items/${itemId}/files/${fileId}/url`),

  deleteAdminTripItemFile: (tripId: string, itemId: string, fileId: string) =>
    request<{ success: boolean }>(`/admin/trips/${tripId}/items/${itemId}/files/${fileId}`, {
      method: 'DELETE',
    }),

  // Blog (Public)
  getBlogPosts: (options?: { page?: number; per_page?: number; category?: string; tag?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.per_page) params.set('per_page', String(options.per_page));
    if (options?.category) params.set('category', options.category);
    if (options?.tag) params.set('tag', options.tag);
    const qs = params.toString();
    return request<BlogPostsResponse>(`/blog/posts${qs ? `?${qs}` : ''}`);
  },

  getBlogPost: (slug: string) =>
    request<BlogPost>(`/blog/posts/${slug}`),

  getBlogCategories: () =>
    request<BlogCategory[]>('/blog/categories'),

  /**
   * A trip template collection for the blog's itinerary block. Articles
   * normally arrive with their collections already in the payload, so this is
   * the fallback path for a block rendered outside one.
   */
  getTripTemplateCollection: (slug: string) =>
    request<TripTemplateCollection>(`/trip-templates/collections/${slug}`),

  /**
   * The route somebody arrived from, for the intake form's starting-point card
   * and the destination it fills in. Both arguments come off a URL, so both
   * are encoded here rather than trusted.
   */
  getTripTemplateStartingPoint: (slug: string, variantKey?: string) =>
    request<TripTemplateStartingPoint>(
      `/trip-templates/${encodeURIComponent(slug)}/starting-point` +
        (variantKey ? `?variant=${encodeURIComponent(variantKey)}` : ''),
    ),

  // Trip template authoring (admin)

  listTripTemplates: () =>
    request<AdminTripTemplateListResponse>('/admin/trip-templates'),

  getTripTemplate: (templateId: string) =>
    request<AdminTripTemplate>(`/admin/trip-templates/${templateId}`),

  createTripTemplate: (data: TripTemplateInput) =>
    request<{ id: string; slug: string }>('/admin/trip-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Partial: a child collection that is present replaces the stored set. */
  updateTripTemplate: (templateId: string, data: Partial<TripTemplateInput>) =>
    request<AdminTripTemplate>(`/admin/trip-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /** A stop or route photograph. Compressed to a web-sized WebP server-side. */
  uploadTripTemplateImage: (formData: FormData) =>
    uploadForm<{ url: string; bytes: number }>('/admin/trip-templates/images', formData),

  duplicateTripTemplate: (templateId: string) =>
    request<{ id: string; slug: string }>(`/admin/trip-templates/${templateId}/duplicate`, {
      method: 'POST',
    }),

  deleteTripTemplate: (templateId: string) =>
    request<void>(`/admin/trip-templates/${templateId}`, { method: 'DELETE' }),

  createTripTemplateCollection: (data: TripTemplateCollectionInput) =>
    request<{ id: string; slug: string }>('/admin/trip-templates/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTripTemplateCollection: (collectionId: string, data: Partial<TripTemplateCollectionInput>) =>
    request<{ id: string; slug: string }>(`/admin/trip-templates/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteTripTemplateCollection: (collectionId: string) =>
    request<void>(`/admin/trip-templates/collections/${collectionId}`, { method: 'DELETE' }),

  /** The reader payload including drafts, so the editor preview is not blank while writing. */
  previewTripTemplateCollection: (collectionId: string) =>
    request<TripTemplateCollection>(`/admin/trip-templates/collections/${collectionId}/preview`),

  getBlogTags: () =>
    request<BlogTag[]>('/blog/tags'),

  // Blog (Admin)
  getAdminBlogPosts: (status?: string) =>
    request<BlogPost[]>(`/admin/blog/posts${status ? `?status=${status}` : ''}`),

  getAdminBlogPost: (id: string) =>
    request<BlogPost>(`/admin/blog/posts/${id}`),

  createBlogPost: (data: CreateBlogPostDTO) =>
    request<BlogPost>('/admin/blog/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBlogPost: (id: string, data: UpdateBlogPostDTO) =>
    request<BlogPost>(`/admin/blog/posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteBlogPost: (id: string) =>
    request<{ success: boolean }>(`/admin/blog/posts/${id}`, {
      method: 'DELETE',
    }),

  publishBlogPost: (id: string) =>
    request<BlogPost>(`/admin/blog/posts/${id}/publish`, {
      method: 'POST',
    }),

  unpublishBlogPost: (id: string) =>
    request<BlogPost>(`/admin/blog/posts/${id}/unpublish`, {
      method: 'POST',
    }),

  /**
   * Submits a generation and returns immediately with a job to poll. Generation
   * takes ~100s, which is far too long to hold a single request open: an idle
   * connection dropped on either hop left the author on a spinner forever.
   */
  startBlogGeneration: (data: GenerateBlogPostDTO) =>
    request<BlogGenerationJob>('/admin/blog/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Follow `job_id` from the response: a retry at a larger budget changes it. */
  getBlogGenerationJob: (jobId: string) =>
    request<BlogGenerationJob>(`/admin/blog/generate/${jobId}`),

  cancelBlogGeneration: (jobId: string) =>
    request<void>(`/admin/blog/generate/${jobId}/cancel`, { method: 'POST' }),

  uploadBlogImage: (formData: FormData) =>
    uploadForm<{ url: string }>('/admin/blog/upload', formData),

  getAdminBlogCategories: () =>
    request<BlogCategory[]>('/admin/blog/categories'),

  createBlogCategory: (data: CreateBlogCategoryDTO) =>
    request<BlogCategory>('/admin/blog/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBlogCategory: (id: string, data: UpdateBlogCategoryDTO) =>
    request<BlogCategory>(`/admin/blog/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteBlogCategory: (id: string) =>
    request<{ success: boolean }>(`/admin/blog/categories/${id}`, {
      method: 'DELETE',
    }),

  getAdminBlogTags: () =>
    request<BlogTag[]>('/admin/blog/tags'),

  createBlogTag: (data: CreateBlogTagDTO) =>
    request<BlogTag>('/admin/blog/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteBlogTag: (id: string) =>
    request<{ success: boolean }>(`/admin/blog/tags/${id}`, {
      method: 'DELETE',
    }),

  getAdminBlogAuthors: () =>
    request<BlogAuthorProfile[]>('/admin/blog/authors'),

  updateBlogAuthor: (id: string, data: UpdateBlogAuthorDTO) =>
    request<BlogAuthorProfile>(`/admin/blog/authors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Newsletter
  subscribeNewsletter: (data: NewsletterSubscribeDTO) =>
    request<{ success: true }>('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  unsubscribeNewsletter: (token: string) =>
    request<{ success: true }>(`/newsletter/unsubscribe/${token}`, {
      method: 'POST',
    }),

  // Notifications
  getNotificationTemplates: () =>
    request<import('../components/trip/notifications/types').NotificationTemplate[]>('/admin/notifications/templates'),

  createNotificationTemplate: (data: Record<string, unknown>) =>
    request<import('../components/trip/notifications/types').NotificationTemplate>('/admin/notifications/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateNotificationTemplate: (id: string, data: Record<string, unknown>) =>
    request<import('../components/trip/notifications/types').NotificationTemplate>(`/admin/notifications/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getTripNotifications: (tripId: string) =>
    request<import('../components/trip/notifications/types').ScheduledNotification[]>(`/admin/notifications/trips/${tripId}/notifications`),

  createTripNotification: (tripId: string, data: Record<string, unknown>) =>
    request<import('../components/trip/notifications/types').ScheduledNotification>(`/admin/notifications/trips/${tripId}/notifications`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTripNotification: (tripId: string, notificationId: string, data: Record<string, unknown>) =>
    request<import('../components/trip/notifications/types').ScheduledNotification>(`/admin/notifications/trips/${tripId}/notifications/${notificationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteTripNotification: (tripId: string, notificationId: string) =>
    request<{ success: boolean }>(`/admin/notifications/trips/${tripId}/notifications/${notificationId}`, {
      method: 'DELETE',
    }),

  sendTripNotificationNow: (tripId: string, notificationId: string) =>
    request<{ success: boolean }>(`/admin/notifications/trips/${tripId}/notifications/${notificationId}/send-now`, {
      method: 'POST',
    }),

  generateNotificationText: (data: import('../components/trip/notifications/types').GenerateNotificationTextRequest) =>
    request<import('../components/trip/notifications/types').GeneratedNotificationText>('/admin/notifications/generate-text', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // === Trip Confirmations (User) ===

  getTripConfirmations: (tripId: string) =>
    request<TripConfirmation[]>(`/trips/${tripId}/confirmations`),

  respondToConfirmation: (tripId: string, confirmationId: string, data: { status: 'confirmed' | 'declined'; note?: string }) =>
    request<TripConfirmation>(`/trips/${tripId}/confirmations/${confirmationId}/respond`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // === Trip Confirmations (Admin) ===

  getAdminTripConfirmations: (tripId: string) =>
    request<TripConfirmation[]>(`/admin/notifications/trips/${tripId}/confirmations`),

  recordAdminConfirmationResponse: (
    tripId: string,
    confirmationId: string,
    data: import('@wanderlust/shared').RecordAdminConfirmationResponseDTO,
  ) =>
    request<import('@wanderlust/shared').TripConfirmationResponseLog>(
      `/admin/notifications/trips/${tripId}/confirmations/${confirmationId}/responses`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  uploadAdminConfirmationEvidence: (
    tripId: string,
    confirmationId: string,
    logId: string,
    files: File[],
  ) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return uploadForm<{ files: import('@wanderlust/shared').ConfirmationEvidenceFile[] }>(
      `/admin/notifications/trips/${tripId}/confirmations/${confirmationId}/responses/${logId}/files`,
      formData,
    );
  },

  getAdminConfirmationEvidenceUrl: (
    tripId: string,
    confirmationId: string,
    logId: string,
    fileId: string,
  ) =>
    request<{ url: string; expires_in: number }>(
      `/admin/notifications/trips/${tripId}/confirmations/${confirmationId}/responses/${logId}/files/${fileId}/url`,
    ),

  deleteAdminConfirmationEvidence: (
    tripId: string,
    confirmationId: string,
    logId: string,
    fileId: string,
  ) =>
    request<{ success: boolean }>(
      `/admin/notifications/trips/${tripId}/confirmations/${confirmationId}/responses/${logId}/files/${fileId}`,
      { method: 'DELETE' },
    ),

  // === Planning Journal ===

  getTripJournalEntries: (tripId: string, filters?: { type?: string; category?: string }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.category) params.set('category', filters.category);
    const qs = params.toString();
    return request<import('@wanderlust/shared').PlanningJournalEntry[]>(`/admin/journal/trips/${tripId}/journal${qs ? '?' + qs : ''}`);
  },

  createJournalEntry: (tripId: string, data: import('@wanderlust/shared').CreateJournalEntryDTO) =>
    request<import('@wanderlust/shared').PlanningJournalEntry>(`/admin/journal/trips/${tripId}/journal`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateJournalEntry: (tripId: string, entryId: string, data: import('@wanderlust/shared').UpdateJournalEntryDTO) =>
    request<import('@wanderlust/shared').PlanningJournalEntry>(`/admin/journal/trips/${tripId}/journal/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteJournalEntry: (tripId: string, entryId: string) =>
    request<{ success: boolean }>(`/admin/journal/trips/${tripId}/journal/${entryId}`, {
      method: 'DELETE',
    }),

  // === Leads ===
  submitLead: (data: CreateLeadDTO) =>
    request<{ success: true; id?: string }>('/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateLeadProfile: (id: string, data: UpdateLeadProfileDTO) =>
    request<{ success: true }>(`/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  submitTripRequest: (data: CreateTripRequestDTO) =>
    request<{ success: true; id?: string }>('/trip-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAdminLeads: (status?: string) =>
    request<Lead[]>(`/admin/leads${status ? `?status=${status}` : ''}`),

  getAdminLead: (id: string) =>
    request<Lead>(`/admin/leads/${id}`),

  /** Adds a lead by hand: a name plus an email, a phone number, or both. */
  createAdminLead: (data: CreateAdminLeadDTO) =>
    request<Lead>('/admin/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAdminLead: (id: string, data: UpdateLeadDTO) =>
    request<Lead>(`/admin/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /** Creates or links the client record a lead becomes, and marks the lead converted. */
  convertLeadToClient: (id: string, data: ConvertLeadToClientDTO = {}) =>
    request<ConvertLeadToClientResponse>(`/admin/leads/${id}/client`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createLeadTripRequest: (leadId: string, data: LeadTripRequestInput) =>
    request<LeadTripRequest>(`/admin/leads/${leadId}/trip-requests`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateLeadTripRequest: (leadId: string, tripRequestId: string, data: LeadTripRequestInput) =>
    request<LeadTripRequest>(`/admin/leads/${leadId}/trip-requests/${tripRequestId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteLeadTripRequest: (leadId: string, tripRequestId: string) =>
    request<{ success: boolean }>(`/admin/leads/${leadId}/trip-requests/${tripRequestId}`, {
      method: 'DELETE',
    }),

  /** Drafts the first outreach email from the lead's CRM data. Saves nothing. */
  draftLeadFirstResponse: (leadId: string, data: DraftLeadFirstResponseDTO = {}) =>
    request<LeadFirstResponseDraft>(`/admin/leads/${leadId}/first-response`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // === Dream capture ===

  getAdminDreams: (params: DreamListQuery = {}) => {
    const query = new URLSearchParams();
    // Everything the report can narrow the captures by. Written out rather than
    // looped so the wire names stay visible at the call site.
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    if (params.days !== undefined) query.set('days', String(params.days));
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.q) query.set('q', params.q);
    if (params.stage) query.set('stage', params.stage);
    if (params.step) query.set('step', params.step);
    if (params.source) query.set('source', params.source);
    if (params.arrival_kind) query.set('arrival_kind', params.arrival_kind);
    if (params.arrival_label) query.set('arrival_label', params.arrival_label);
    if (params.friction) query.set('friction', params.friction);
    if (params.include_flagged) query.set('includeFlagged', 'true');
    return request<DreamCaptureListResponse>(`/admin/dreams?${query}`);
  },

  /** `days` omitted means all time, which is what the report shows by default. */
  /**
   * The browser's zone goes with it so the chart's days are the reader's days.
   * The API falls back to UTC for a zone it does not know.
   */
  getAdminDreamSummary: (days?: number) => {
    const query = new URLSearchParams();
    if (days) query.set('days', String(days));
    try {
      query.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch {
      // No zone to send; the API buckets in UTC.
    }
    const search = query.toString();
    return request<DreamSummaryResponse>(`/admin/dreams/summary${search ? `?${search}` : ''}`);
  },

  // === Communications (Admin) ===
  getAdminConversations: (options?: { filter?: ConversationFilter; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.filter && options.filter !== 'all') params.set('filter', options.filter);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const query = params.toString();
    return request<ConversationsResponse>(
      query ? `/admin/communications/conversations?${query}` : '/admin/communications/conversations'
    );
  },

  getAdminConversation: (id: string) =>
    request<ConversationDetail>(`/admin/communications/conversations/${id}`),

  /** Marks the conversation read, or unread again with `{ unread: true }`. */
  markConversationRead: (id: string, options?: { unread?: boolean }) =>
    request<{ conversation: ConversationDetail['conversation'] }>(`/admin/communications/conversations/${id}/read`, {
      method: 'PUT',
      ...(options?.unread ? { body: JSON.stringify({ unread: true }) } : {}),
    }),

  updateAdminConversation: (id: string, data: UpdateConversationDTO) =>
    request<ConversationDetail>(`/admin/communications/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  sendConversationMessage: (id: string, data: SendConversationMessageDTO) =>
    request<{ communication: Communication }>(`/admin/communications/conversations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCommunicationTriage: (id: string, data: UpdateTriageDTO) =>
    request<{ triage: CommunicationTriage }>(`/admin/communications/messages/${id}/triage`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  resolveCommunication: (id: string) =>
    request<{ triage: CommunicationTriage; pagerduty_resolved: boolean }>(`/admin/communications/messages/${id}/resolve`, {
      method: 'POST',
    }),

  getCommunicationAudio: (id: string) =>
    request<CommunicationAudioResponse>(`/admin/communications/messages/${id}/audio`),
};

// === Dream capture (public, fire-and-forget) ===
//
// These bypass `request` on purpose. The endpoints answer 204 with no body, so
// `response.json()` would throw, and nothing here is worth surfacing to a caller:
// a failed capture must never interrupt someone trying to plan a trip.

/** Records a Start Planning press. `keepalive` lets it outlive the navigation it starts. */
export function captureDream(data: DreamCaptureRequest, keepalive = false): void {
  fetch(`${API_BASE}/dreams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    keepalive,
  }).catch(() => {
    // capture is best-effort
  });
}

/**
 * Reports what the form knows about the capture a Start Planning press began:
 * the stage it reached, the dream as written in the trip field, and anything
 * that got in the way. Sent with `keepalive`, so a report made as the page goes
 * away still arrives.
 */
export function reportDream(token: string, report: DreamStageRequest): void {
  fetch(`${API_BASE}/dreams/${token}/stage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
    // A friction is often the last thing that happens before the tab goes away.
    keepalive: true,
  }).catch(() => {
    // best-effort
  });
}

// Helper to build place photo URL (not an API call, just URL construction).
// `index` is the photo's position in the item's gallery: if the stored
// reference has expired, the server heals it to the live photo at the same
// position instead of collapsing every gallery slot onto the first photo.
export function getPlacePhotoUrl(
  placeId: string,
  photoReference: string,
  width?: number,
  index?: number
): string {
  const params = new URLSearchParams({ ref: photoReference });
  if (width) params.set('w', String(width));
  if (index) params.set('i', String(index));
  return `${API_BASE}/places/photo/${placeId}?${params}`;
}

// Position of a photo reference in an item's stored gallery (either shape),
// for getPlacePhotoUrl's `index` argument. A hero URL must carry the same
// index as its gallery counterpart so the two URLs stay identical.
export function findPhotoReferenceIndex(
  photoReferences: Array<string | { name: string }> | undefined,
  reference: string | undefined
): number | undefined {
  if (!reference || !photoReferences) return undefined;
  const index = photoReferences.findIndex((photo) =>
    typeof photo === 'string' ? photo === reference : photo.name === reference
  );
  return index >= 0 ? index : undefined;
}

export { ApiError, broadcastTokensToExtension, request };
